/**
 * DISCOM Monitoring — 5 KPI cards + sub-division compliance + division compliance.
 * FR-D11 to FR-D14
 */
import React, { useMemo, useState } from 'react';
import KPICard from '../../components/shared/KPICard';
import { pgvclSubDivMonitoring, pgvclDivMonitoring, pgvclOverview } from '../../data/mockData';

const ALL_CIRCLES = Array.from(new Set(pgvclSubDivMonitoring.map(r => r.circle))).sort();

const divisionsByCircle = ALL_CIRCLES.reduce<Record<string, string[]>>((acc, c) => {
  acc[c] = Array.from(
    new Set(pgvclSubDivMonitoring.filter(r => r.circle === c).map(r => r.division))
  ).sort();
  return acc;
}, {});

function txCls(v: number) {
  return v >= 90 ? 'text-green-600 font-semibold' :
         v >= 70 ? 'text-amber-600 font-semibold' :
                   'text-red-600 font-semibold';
}

export default function DiscomMonitoring() {
  const [sdCircle,   setSdCircle]   = useState('');
  const [sdDivision, setSdDivision] = useState('');
  const [divCircle,  setDivCircle]  = useState('');

  const sdDivisionsForCircle = sdCircle ? (divisionsByCircle[sdCircle] ?? []) : [];

  const sdRows = useMemo(() => {
    let rows = pgvclSubDivMonitoring;
    if (sdCircle)   rows = rows.filter(r => r.circle   === sdCircle);
    if (sdDivision) rows = rows.filter(r => r.division === sdDivision);
    return rows;
  }, [sdCircle, sdDivision]);

  const divRows = useMemo(() => {
    let rows = pgvclDivMonitoring;
    if (divCircle) rows = rows.filter(r => r.circle === divCircle);
    return rows;
  }, [divCircle]);

  // KPI aggregates from raw data
  const activeAlarms   = 58;
  const criticalAlarms = 8;
  const plantsOffline  = pgvclOverview.offlinePlants;
  const avgDataTx      = parseFloat(
    (pgvclSubDivMonitoring.reduce((s, r) => s + r.dataTxPercent, 0) / pgvclSubDivMonitoring.length).toFixed(1)
  );
  const certsExpiring  = pgvclSubDivMonitoring.reduce((s, r) => s + r.certsExpiring, 0);

  return (
    <div className="p-6 space-y-6">

      {/* ── 5 KPI cards ── */}
      <div className="flex gap-4">
        <KPICard
          title="Active Alarms"
          value={activeAlarms.toString()}
          subtext="PGVCL wide"
          subtextColor="red"
          bgColor="bg-red-50"
        />
        <KPICard
          title="Critical Alarms"
          value={criticalAlarms.toString()}
          subtext="Priority 1"
          subtextColor="red"
          bgColor="bg-red-50"
        />
        <KPICard
          title="Plants Offline"
          value={plantsOffline.toLocaleString()}
          subtext="Requires attention"
          subtextColor="red"
          bgColor="bg-red-50"
        />
        <KPICard
          title="Data Transmission"
          value={`${avgDataTx}%`}
          subtext="Avg across sub-divisions"
          subtextColor={avgDataTx >= 90 ? 'green' : avgDataTx >= 70 ? 'default' : 'red'}
        />
        <KPICard
          title="Certs Expiring ≤30d"
          value={certsExpiring.toString()}
          subtext="Across PGVCL"
          subtextColor={certsExpiring > 5 ? 'red' : certsExpiring > 0 ? 'default' : 'green'}
        />
      </div>

      {/* ── Sub-Division compliance table ── */}
      <div className="bg-white rounded-card border border-border shadow-card overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center gap-3">
          <p className="text-xs font-semibold text-text-primary">Sub-Division Compliance</p>
          <select
            value={sdCircle}
            onChange={e => { setSdCircle(e.target.value); setSdDivision(''); }}
            className="text-xs border border-border rounded-input px-2.5 py-1 bg-white focus:outline-none focus:border-accent ml-auto"
          >
            <option value="">All Circles</option>
            {ALL_CIRCLES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            value={sdDivision}
            onChange={e => setSdDivision(e.target.value)}
            disabled={!sdCircle}
            className="text-xs border border-border rounded-input px-2.5 py-1 bg-white focus:outline-none focus:border-accent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="">All Divisions</option>
            {sdDivisionsForCircle.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bg-secondary border-b border-border">
              <tr>
                <th className="text-left py-2.5 px-4 text-text-secondary font-semibold">Sub-Division</th>
                <th className="text-left py-2.5 px-3 text-text-secondary font-semibold">Circle</th>
                <th className="text-left py-2.5 px-3 text-text-secondary font-semibold">Division</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Data Tx %</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Plants Offline</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Active Alarms</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Certs Expiring</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Firmware Outdated</th>
              </tr>
            </thead>
            <tbody>
              {sdRows.map(r => (
                <tr key={r.subDivision} className="border-b border-border/50 hover:bg-bg-secondary transition-colors">
                  <td className="py-2.5 px-4 font-medium text-text-primary">{r.subDivision}</td>
                  <td className="py-2.5 px-3 text-text-tertiary">{r.circle}</td>
                  <td className="py-2.5 px-3 text-text-tertiary">{r.division}</td>
                  <td className={`py-2.5 px-3 text-right ${txCls(r.dataTxPercent)}`}>{r.dataTxPercent.toFixed(1)}%</td>
                  <td className={`py-2.5 px-3 text-right ${r.plantsOffline > 0 ? 'text-red-600 font-semibold' : 'text-text-secondary'}`}>{r.plantsOffline}</td>
                  <td className={`py-2.5 px-3 text-right ${r.activeAlarms > 5 ? 'text-amber-600 font-semibold' : 'text-text-secondary'}`}>{r.activeAlarms}</td>
                  <td className={`py-2.5 px-3 text-right ${r.certsExpiring > 0 ? 'text-amber-600 font-semibold' : 'text-text-secondary'}`}>{r.certsExpiring}</td>
                  <td className={`py-2.5 px-3 text-right ${r.firmwareOutdated > 0 ? 'text-text-secondary' : 'text-green-600'}`}>{r.firmwareOutdated}</td>
                </tr>
              ))}
              {sdRows.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-xs text-text-tertiary">No data for selected filters.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Division compliance table ── */}
      <div className="bg-white rounded-card border border-border shadow-card overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center gap-3">
          <p className="text-xs font-semibold text-text-primary">Division Compliance</p>
          <select
            value={divCircle}
            onChange={e => setDivCircle(e.target.value)}
            className="text-xs border border-border rounded-input px-2.5 py-1 bg-white focus:outline-none focus:border-accent ml-auto"
          >
            <option value="">All Circles</option>
            {ALL_CIRCLES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bg-secondary border-b border-border">
              <tr>
                <th className="text-left py-2.5 px-4 text-text-secondary font-semibold">Division</th>
                <th className="text-left py-2.5 px-3 text-text-secondary font-semibold">Circle</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Data Tx %</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Plants Offline</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Active Alarms</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Certs Expiring</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Firmware Outdated</th>
              </tr>
            </thead>
            <tbody>
              {divRows.map(r => (
                <tr key={`${r.circle}-${r.division}`} className="border-b border-border/50 hover:bg-bg-secondary transition-colors">
                  <td className="py-2.5 px-4 font-medium text-text-primary">{r.division}</td>
                  <td className="py-2.5 px-3 text-text-tertiary">{r.circle}</td>
                  <td className={`py-2.5 px-3 text-right ${txCls(r.dataTxPercent)}`}>{r.dataTxPercent.toFixed(1)}%</td>
                  <td className={`py-2.5 px-3 text-right ${r.plantsOffline > 0 ? 'text-red-600 font-semibold' : 'text-text-secondary'}`}>{r.plantsOffline}</td>
                  <td className={`py-2.5 px-3 text-right ${r.activeAlarms > 10 ? 'text-amber-600 font-semibold' : 'text-text-secondary'}`}>{r.activeAlarms}</td>
                  <td className={`py-2.5 px-3 text-right ${r.certsExpiring > 0 ? 'text-amber-600 font-semibold' : 'text-text-secondary'}`}>{r.certsExpiring}</td>
                  <td className={`py-2.5 px-3 text-right ${r.firmwareOutdated > 0 ? 'text-text-secondary' : 'text-green-600'}`}>{r.firmwareOutdated}</td>
                </tr>
              ))}
              {divRows.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-xs text-text-tertiary">No data for selected circle.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
